import React, { useEffect, useState } from 'react';
import { admin } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, Zap, AlertTriangle, Check, X, Clock, Target, Building2, Calendar } from 'lucide-react';
import AdminBlackoutIntroVideoEditor from './AdminBlackoutIntroVideoEditor';

const QUEUES = [
  { key: 'pending_review', label: 'Pending Alerts', tint: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/40' },
  { key: 'all',             label: 'All Submissions', tint: 'text-neutral-300', bg: 'bg-neutral-500/10 border-neutral-500/40' },
  { key: 'approved',        label: 'Approved',       tint: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/40' },
  { key: 'rejected',        label: 'Rejected',       tint: 'text-red-400',     bg: 'bg-red-500/10 border-red-500/40' },
  { key: 'duplicate',       label: 'Duplicates',     tint: 'text-sky-400',     bg: 'bg-sky-500/10 border-sky-500/40' },
];

export default function AdminBlackoutPanel() {
  const [queue, setQueue] = useState('pending_review');
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // submission id currently being reviewed
  const [notes, setNotes] = useState({});  // id → draft note text

  const load = async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        admin.blackoutStats(),
        admin.blackoutList(queue),
      ]);
      setStats(s); setRows(list);
    } catch (e) {
      toast({ title: 'Load failed', description: e?.response?.data?.detail || 'Try again' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [queue]);

  const review = async (sub, action) => {
    setBusy(sub.id);
    try {
      const note = (notes[sub.id] || '').trim();
      const res = await admin.blackoutReview(sub.id, { action, note: note || undefined });
      toast({
        title: `Submission ${res.status}`,
        description: res.pointsDelta ? `+${res.pointsDelta} points awarded` : undefined,
      });
      // Clear the drafted note so the next pass starts clean
      setNotes(n => { const next = { ...n }; delete next[sub.id]; return next; });
      load();
    } catch (e) {
      toast({ title: 'Review failed', description: e?.response?.data?.detail || 'Try again' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-blackout-panel">
      <AdminBlackoutIntroVideoEditor />
      <div className="flex items-center gap-3">
        <Zap className="text-emerald-400" size={20}/>
        <div>
          <h2 className="font-bold text-lg">Blackout Game — Review Queue</h2>
          <p className="text-xs text-neutral-400">Approve or reject member-submitted discoveries. Approvals award points retroactively.</p>
        </div>
      </div>

      {/* Queue tabs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {QUEUES.map(q => {
          const count = stats?.[q.key] ?? 0;
          const active = queue === q.key;
          return (
            <button
              key={q.key}
              onClick={() => setQueue(q.key)}
              data-testid={`admin-blackout-tab-${q.key}`}
              className={`rounded-xl px-4 py-3 border text-left transition ${active ? q.bg + ' ring-2 ring-white/10' : 'border-neutral-800 hover:border-neutral-600 bg-neutral-900/40'}`}
            >
              <div className={`text-[10px] uppercase tracking-wider font-black ${active ? q.tint : 'text-neutral-400'}`}>{q.label}</div>
              <div className={`text-2xl font-black tabular-nums ${active ? 'text-white' : 'text-neutral-200'}`} data-testid={`admin-blackout-count-${q.key}`}>{count}</div>
            </button>
          );
        })}
      </div>

      {/* Queue rows */}
      {loading ? (
        <div className="pbb-card p-10 text-center"><Loader2 className="mx-auto animate-spin text-emerald-400" size={22}/></div>
      ) : rows.length === 0 ? (
        <div className="pbb-card p-10 text-center text-neutral-400 text-sm" data-testid="admin-blackout-empty">
          No submissions in this queue.
        </div>
      ) : (
        <div className="pbb-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-900 text-left text-[10px] uppercase tracking-wider text-neutral-400">
              <tr>
                <th className="px-4 py-3">Submission</th>
                <th className="px-4 py-3">Member</th>
                <th className="px-4 py-3">Flag</th>
                <th className="px-4 py-3">Points</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-neutral-800 align-top" data-testid={`admin-blackout-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-2">
                      {r.type === 'business' ? <Building2 size={14} className="text-emerald-400 shrink-0 mt-0.5"/> : <Calendar size={14} className="text-fuchsia-400 shrink-0 mt-0.5"/>}
                      <div>
                        <div className="font-semibold truncate max-w-xs">{r?.data?.name || '—'}</div>
                        <div className="text-[11px] text-neutral-500 mt-0.5 truncate max-w-xs">
                          {r?.data?.city && r?.data?.state ? `${r.data.city}, ${r.data.state}` : ''}
                          {r?.data?.eventDate ? ` · ${r.data.eventDate}` : ''}
                        </div>
                        {r?.data?.sourceUrl && (
                          <a href={r.data.sourceUrl} target="_blank" rel="noreferrer" className="text-[11px] text-emerald-400 hover:underline">source ↗</a>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-neutral-300">{r.submittedByName || <span className="text-neutral-500">—</span>}</td>
                  <td className="px-4 py-3">
                    {r.flagReason ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-200 text-[11px] font-semibold">
                        <AlertTriangle size={10}/> {r.flagReason.replace(/_/g,' ')}
                      </span>
                    ) : <span className="text-neutral-500 text-[11px]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-amber-300 font-bold tabular-nums">+{r.pointsAwarded || 0}</td>
                  <td className="px-4 py-3 text-neutral-400 text-[11px]">{r.createdAt?.slice(0,10)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col items-end gap-1.5 min-w-[220px]">
                      {/* Append-only audit trail — most recent on top */}
                      {Array.isArray(r.reviewHistory) && r.reviewHistory.length > 0 && (
                        <div className="w-full text-[10px] text-neutral-400 bg-black/40 border border-neutral-800 rounded px-2 py-1.5 text-left max-h-24 overflow-y-auto space-y-1" data-testid={`admin-blackout-history-${r.id}`}>
                          <div className="uppercase tracking-wider text-neutral-500 font-black not-italic text-[9px]">History ({r.reviewHistory.length})</div>
                          {[...r.reviewHistory].reverse().slice(0, 5).map((h, i) => (
                            <div key={i} className="border-l-2 border-emerald-500/40 pl-2">
                              <span className="font-bold text-emerald-300">{h.action}</span>
                              <span className="text-neutral-500 ml-1">· {h.byName}</span>
                              <span className="text-neutral-500 ml-1">· {(h.at || '').slice(0, 10)}</span>
                              {h.note && <div className="italic text-neutral-300 mt-0.5">"{h.note}"</div>}
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Draft note — only sent on the next action click */}
                      <textarea
                        value={notes[r.id] || ''}
                        onChange={e => setNotes(n => ({ ...n, [r.id]: e.target.value }))}
                        placeholder="Internal note (optional)"
                        rows={2}
                        data-testid={`admin-blackout-note-${r.id}`}
                        className="w-full bg-black border border-neutral-800 rounded-md px-2 py-1 text-[11px] text-neutral-200 placeholder-neutral-600 focus:border-emerald-500 outline-none resize-none"
                      />
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <ActionBtn label="Approve" testId={`approve-${r.id}`} tone="emerald" icon={Check} disabled={busy===r.id} onClick={() => review(r,'approve')}/>
                        <ActionBtn label="Pending" testId={`pending-${r.id}`} tone="amber"   icon={Clock} disabled={busy===r.id} onClick={() => review(r,'pending')}/>
                        <ActionBtn label="Dup"     testId={`duplicate-${r.id}`} tone="sky"     icon={Target} disabled={busy===r.id} onClick={() => review(r,'duplicate')}/>
                        <ActionBtn label="Reject"  testId={`reject-${r.id}`} tone="red"     icon={X}     disabled={busy===r.id} onClick={() => review(r,'reject')}/>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ActionBtn({ label, icon: Icon, tone, disabled, onClick, testId }) {
  const colors = {
    emerald: 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15',
    amber: 'border-amber-500/40 text-amber-300 hover:bg-amber-500/15',
    sky: 'border-sky-500/40 text-sky-300 hover:bg-sky-500/15',
    red: 'border-red-500/40 text-red-300 hover:bg-red-500/15',
  }[tone];
  return (
    <button data-testid={testId} onClick={onClick} disabled={disabled}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md border text-[11px] font-bold uppercase tracking-wider ${colors} disabled:opacity-40`}>
      <Icon size={10}/> {label}
    </button>
  );
}
